import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Wheat, Plus, TrendingUp, Coins, Download, Eye } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

const ADMIN = ["super_admin", "property_manager"];
const CURRENT_YEAR = new Date().getFullYear();

export default function YieldsPage() {
  return <YieldsPageInner />;
}

function StatementArchive({ stmtYear, setStmtYear, stmtMonth, setStmtMonth, downloadStatement, downloading, openPreview }) {
  const [available, setAvailable] = useState([]);
  useEffect(() => { api.get("/owner-statements/available").then((r) => setAvailable(r.data)).catch(() => {}); }, []);
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const pick = async (y, m) => {
    setStmtYear(y); setStmtMonth(m);
    setTimeout(() => openPreview(), 50);
  };
  return (
    <div className="mb-6 p-4 rounded-lg border border-border bg-card" data-testid="statement-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-1">Monthly statement</div>
          <div className="text-sm text-muted-foreground">Preview or download a PDF combining yield payouts, cottage bookings and maintenance for a specific month.</div>
        </div>
        <div>
          <Label>Year</Label>
          <Input type="number" min="2024" max="2100" value={stmtYear} onChange={(e) => setStmtYear(Number(e.target.value))} className="w-24" data-testid="stmt-year-input" />
        </div>
        <div>
          <Label>Month</Label>
          <Select value={String(stmtMonth)} onValueChange={(v) => setStmtMonth(Number(v))}>
            <SelectTrigger className="w-32" data-testid="stmt-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthNames.map((m, i) => (<SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={openPreview} data-testid="preview-statement-btn">
          <Eye size={14} className="mr-2" />Preview
        </Button>
        <Button onClick={downloadStatement} disabled={downloading} data-testid="download-statement-btn">
          <Download size={14} className="mr-2" />{downloading ? "Preparing…" : "Download PDF"}
        </Button>
      </div>
      {available.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">Archive · tap to preview</div>
          <div className="flex flex-wrap gap-2" data-testid="statement-archive">
            {available.map(({ year, month }) => (
              <button key={`${year}-${month}`} type="button" onClick={() => pick(year, month)}
                      className="text-xs border border-border rounded-md px-3 py-1.5 hover:bg-secondary/40"
                      data-testid={`archive-${year}-${month}`}>
                {monthNames[month - 1]} {year}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function YieldsPageInner() {
  const { user } = useAuth();
  const admin = ADMIN.includes(user?.role);
  const [items, setItems] = useState([]);
  const [plots, setPlots] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    plot_id: "", year: CURRENT_YEAR, quarter: 1, crop: "Mango",
    yield_kg: 0, revenue: 0, payout_share: 0.7, notes: "",
  });

  const load = async () => {
    const [y, p] = await Promise.all([api.get("/yields"), api.get("/plots")]);
    setItems(y.data); setPlots(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/yields", {
        ...form,
        year: Number(form.year), quarter: Number(form.quarter),
        yield_kg: Number(form.yield_kg), revenue: Number(form.revenue),
        payout_share: Number(form.payout_share),
      });
      toast.success("Yield recorded");
      setOpen(false);
      setForm({ plot_id: "", year: CURRENT_YEAR, quarter: 1, crop: "Mango", yield_kg: 0, revenue: 0, payout_share: 0.7, notes: "" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const totals = useMemo(() => {
    const t = items.reduce((a, b) => ({
      kg: a.kg + (b.yield_kg || 0),
      revenue: a.revenue + (b.revenue || 0),
      payout: a.payout + (b.owner_payout || 0),
    }), { kg: 0, revenue: 0, payout: 0 });
    return t;
  }, [items]);

  const chartData = useMemo(() => {
    const keys = [...new Set(items.map((i) => `Q${i.quarter} ${i.year}`))].sort();
    return keys.map((k) => {
      const list = items.filter((i) => `Q${i.quarter} ${i.year}` === k);
      return {
        period: k,
        payout: list.reduce((s, x) => s + (x.owner_payout || 0), 0),
        yield_kg: list.reduce((s, x) => s + (x.yield_kg || 0), 0),
      };
    });
  }, [items]);

  const now = new Date();
  const [stmtYear, setStmtYear] = useState(now.getFullYear());
  const [stmtMonth, setStmtMonth] = useState(now.getMonth() + 1);
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL;
  const statementUrl = `${backend}/api/owner-statement/${stmtYear}/${stmtMonth}`;
  const openPreview = async () => {
    if (previewSrc) { URL.revokeObjectURL(previewSrc); }
    setPreviewSrc("");
    setPreviewOpen(true);
    try {
      const res = await fetch(statementUrl, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      setPreviewSrc(URL.createObjectURL(blob));
    } catch (e) {
      toast.error("Could not load preview");
      setPreviewOpen(false);
    }
  };
  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${backend}/api/owner-statement/${stmtYear}/${stmtMonth}`, { credentials: "include" });
      if (!res.ok) throw new Error("Statement download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `owner-statement-${stmtYear}-${String(stmtMonth).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e.message || "Could not download statement");
    } finally { setDownloading(false); }
  };

  const KPI = ({ label, value, icon: Icon, testid }) => (
    <div className="p-6 rounded-lg border border-border bg-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">{label}</div>
        <Icon size={16} className="text-primary" />
      </div>
      <div className="heading text-3xl mt-4">{value}</div>
    </div>
  );

  const fmtInr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Wheat className="text-primary" />
          <h1 className="heading text-3xl md:text-4xl">Yield & payouts</h1>
        </div>
        {admin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-yield-btn"><Plus size={14} className="mr-2" />Record yield</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record quarterly yield</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Plot</Label>
                  <Select value={form.plot_id} onValueChange={(v) => setForm({ ...form, plot_id: v })}>
                    <SelectTrigger data-testid="yield-plot-select"><SelectValue placeholder="Select plot" /></SelectTrigger>
                    <SelectContent>{plots.map((p) => (<SelectItem key={p.id} value={p.id}>Plot {p.plot_number}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Year</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="yield-year-input" /></div>
                  <div>
                    <Label>Quarter</Label>
                    <Select value={String(form.quarter)} onValueChange={(v) => setForm({ ...form, quarter: v })}>
                      <SelectTrigger data-testid="yield-quarter-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{[1, 2, 3, 4].map((q) => (<SelectItem key={q} value={String(q)}>Q{q}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Crop</Label><Input value={form.crop} onChange={(e) => setForm({ ...form, crop: e.target.value })} data-testid="yield-crop-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Yield (kg)</Label><Input type="number" step="0.1" value={form.yield_kg} onChange={(e) => setForm({ ...form, yield_kg: e.target.value })} data-testid="yield-kg-input" /></div>
                  <div><Label>Revenue (₹)</Label><Input type="number" step="0.01" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} data-testid="yield-revenue-input" /></div>
                </div>
                <div><Label>Owner share (0-1)</Label><Input type="number" step="0.05" min="0" max="1" value={form.payout_share} onChange={(e) => setForm({ ...form, payout_share: e.target.value })} data-testid="yield-share-input" /></div>
                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter><Button type="submit" disabled={!form.plot_id} data-testid="yield-save-btn">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <KPI label="Total yield" value={`${Math.round(totals.kg).toLocaleString()} kg`} icon={Wheat} testid="kpi-yield-kg" />
        <KPI label="Total revenue" value={fmtInr(totals.revenue)} icon={TrendingUp} testid="kpi-revenue" />
        <KPI label={admin ? "Owner payouts" : "Your payout"} value={fmtInr(totals.payout)} icon={Coins} testid="kpi-payout" />
      </div>

      {chartData.length > 0 && (
        <div className="p-6 rounded-lg border border-border bg-card mb-8">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-4">Quarterly payout trend</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="period" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip formatter={(v) => fmtInr(v)} />
                <Bar dataKey="payout" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <h2 className="heading text-xl mb-3">Records</h2>
      <StatementArchive stmtYear={stmtYear} setStmtYear={setStmtYear} stmtMonth={stmtMonth} setStmtMonth={setStmtMonth} downloadStatement={downloadStatement} downloading={downloading} openPreview={openPreview} statementUrl={statementUrl} />
      <Dialog open={previewOpen} onOpenChange={(o) => {
        setPreviewOpen(o);
        if (!o && previewSrc) { URL.revokeObjectURL(previewSrc); setPreviewSrc(""); }
      }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0" data-testid="statement-preview-modal">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Statement · {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][stmtMonth - 1]} {stmtYear}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden px-6">
            {previewOpen && previewSrc ? (
              <iframe title="Owner statement preview" src={previewSrc} className="w-full h-full border border-border rounded-md bg-secondary/20" data-testid="statement-preview-iframe" />
            ) : previewOpen ? (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground border border-border rounded-md">Preparing preview…</div>
            ) : null}
          </div>
          <DialogFooter className="px-6 pb-6">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="statement-preview-close">Close</Button>
            <Button onClick={downloadStatement} disabled={downloading} data-testid="statement-preview-download">
              <Download size={14} className="mr-2" />{downloading ? "Preparing…" : "Download PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="border border-border rounded-lg bg-card overflow-x-auto" data-testid="yields-table">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="p-3">Period</th><th className="p-3">Plot</th><th className="p-3">Crop</th>
              <th className="p-3 text-right">Yield</th><th className="p-3 text-right">Revenue</th><th className="p-3 text-right">{admin ? "Owner payout" : "Your payout"}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((y) => {
              const plot = plots.find((p) => p.id === y.plot_id);
              return (
                <tr key={y.id} data-testid={`yield-row-${y.id}`}>
                  <td className="p-3 font-medium">Q{y.quarter} {y.year}</td>
                  <td className="p-3 text-muted-foreground">{plot?.plot_number || "—"}</td>
                  <td className="p-3">{y.crop}</td>
                  <td className="p-3 text-right">{y.yield_kg} kg</td>
                  <td className="p-3 text-right">{fmtInr(y.revenue)}</td>
                  <td className="p-3 text-right font-medium">{fmtInr(y.owner_payout)}</td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">No yield data yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
